<script lang="ts" setup>
import { computed, ref, type PropType } from 'vue'
import { useRoute } from 'vue-router'
import { filter, intersection, map, replace, size } from 'lodash'
import SIDE_NAV from '@/constants/nav/side'
import { useMe } from '@/queries/me'
import { usePerson } from '@/queries/people/person'
import { useMessagingOrganization } from '@/queries/messages/organization'
import SideNavMobile from '@/layouts/components/SideNavMobile.vue'
import SideNavMenu from '@/layouts/components/SideNavMenu.vue'
import type { SideNavItem, SideNavLocation } from '@/types/sideNav'
import { toInteger } from 'lodash'
import queryClient from '@/plugins/query'
import type { PersonDetailResource } from '@/services/api/client'

const route = useRoute()
const { data: me, isPaid, hasGiving, hasTithelyCustomerId } = useMe()

const features = computed(() => me.value?.features ?? [])
const roles = computed(() => me.value?.roles ?? [])

const props = defineProps({
    menu: {
        type: String as PropType<SideNavLocation>,
        required: true
    }
})

// The person "Messages" tab additionally requires the `messaging_integration`
// feature AND the org to have SMS messaging turned on in the Messaging service.
// Only fetch that org record when it can matter — the person menu, for an org
// whose feature flags already expose the tab — so we don't make a Hub round-trip
// on every other menu.
const hasMessagingIntegration = computed(() => features.value.includes(`messaging_integration`))
const messagingTabPossible = computed(
    () => props.menu === `person` && features.value.includes(`vue-2way-messaging`) && hasMessagingIntegration.value
)
const { data: messagingOrg } = useMessagingOrganization(messagingTabPossible)
const smsMessagingEnabled = computed(
    () => Boolean(messagingOrg.value?.messaging_enabled) && Boolean(messagingOrg.value?.channels?.sms?.enabled)
)

// The person record the Person page already loads, subscribed to by matching its
// query key. `enabled: false` makes this a read-only cache subscription: reactive
// to that query's data, but never issuing a request of its own. The plain
// `queryClient.getQueryData` used for nav counts below is *not* reactive, so it
// can't drive visibility — it would be evaluated before the person has loaded and
// never re-run.
const navPersonId = computed(() => toInteger(route.params?.personId))
const navPersonFields = ref(`[profile_fields],[family],[nav_quantities],[notes],[tags]`)
const { data: navPerson } = usePerson(navPersonId, ref(false), navPersonFields)

// Messaging keys conversations by the person's Messaging-service `mds_id`. Without
// one there is no conversation to open and nothing to send to, so the tab is hidden
// rather than leading somewhere inert.
const personHasMdsId = computed(() => Boolean(navPerson.value?.mdsId))

const maxVisibleItems = ref<number | undefined>(undefined)

const isViewingOwnProfile = computed(() => {
    const personId = toInteger(route.params?.personId)
    const linkedPersonId = me.value?.linkedPersonId ?? 0
    return personId > 0 && linkedPersonId > 0 && personId === linkedPersonId
})

const filterRolesFeaturesAndPaid = computed(() => {
    const userRoles = roles.value
    const userFeatures = features.value
    const orgIsPaid = isPaid.value

    return filter(SIDE_NAV[props.menu], (item: SideNavItem) => {
        const positiveFeatures = item.features ? item.features.filter(feature => !feature.startsWith('!')) : []
        const negativeFeatures = item.features
            ? item.features.filter(feature => feature.startsWith('!')).map(feature => feature.substring(1))
            : []

        let hasRequireRoles = !item.roles || Boolean(size(intersection(item.roles, userRoles)))

        // Special case: Groups sidebar shows if user is viewing their own profile
        if (item.label === 'Groups' && props.menu === 'person' && isViewingOwnProfile.value) {
            hasRequireRoles = true
        }

        // Special case: the person Messages tab also requires the messaging_integration
        // feature, SMS messaging enabled for the org, and an `mds_id` on the person.
        // Fail closed — hidden until every check confirms it's on.
        if (
            item.label === 'Messages' &&
            props.menu === 'person' &&
            (!hasMessagingIntegration.value || !smsMessagingEnabled.value || !personHasMdsId.value)
        ) {
            return false
        }

        const hasRequireFeatures =
            !positiveFeatures.length || Boolean(size(intersection(positiveFeatures, userFeatures)))
        const hasNoNegativeFeatures = !negativeFeatures.length || !size(intersection(negativeFeatures, userFeatures))

        const isPaid = !item.isPaid || orgIsPaid === item.isPaid

        if (item.isTithelyGivingEnabled !== undefined && item.isTithelyGivingEnabled !== hasGiving.value) {
            return false
        }

        if (item.hasTithelyCustomerId !== undefined && item.hasTithelyCustomerId !== hasTithelyCustomerId.value) {
            return false
        }

        return hasRequireRoles && hasRequireFeatures && hasNoNegativeFeatures && isPaid
    })
})

const sideNav = computed(() =>
    map(filterRolesFeaturesAndPaid.value, item => {
        switch (props.menu) {
            case `events`:
                return {
                    ...item,
                    route: replace(item.route, `{instanceId}`, (route.params?.instanceId as string) || ``)
                }
            case `bulk`:
                maxVisibleItems.value = 5
                return item
            case `person`: {
                maxVisibleItems.value = 5
                const personId = toInteger(route.params?.personId)
                const fields = `[profile_fields],[family],[nav_quantities],[notes],[tags]`
                const queryKey = [`people`, [personId, fields]]

                // Get cached person data
                const personData = queryClient.getQueryData<PersonDetailResource>(queryKey)
                const navQuantities = personData?.nav_quantities

                let count: number | undefined
                switch (item.label) {
                    case 'Attendance':
                        count = navQuantities?.events ?? 0
                        break
                    case 'Giving':
                        count = navQuantities?.payments ?? 0
                        break
                    case 'Notes':
                        count = personData?.notes?.length ?? 0
                        break
                    case 'Forms':
                        count = navQuantities?.forms ?? 0
                        break
                    case 'Groups':
                        count = navQuantities?.groups ?? 0
                        break
                    case 'Tags':
                        count = navQuantities?.tags ?? 0
                        break
                    default:
                        count = undefined
                }

                return {
                    ...item,
                    route: replace(item.route, `{personId}`, (route.params?.personId as string) || ``),
                    count
                }
            }
            default:
                return item
        }
    })
)
</script>

<template>
    <SideNavMobile :nav="sideNav" />
    <SideNavMenu
        :nav="sideNav"
        :max-visible-items="maxVisibleItems" />
</template>
